import type { CanvasChatMessage } from './canvas-types';

export interface DesktopAgentMessage {
  id: string;
  kind: 'user' | 'assistant' | 'tool' | 'error';
  text: string;
  label?: string;
  timestamp?: number;
}
export interface DesktopAgentApproval {
  id: string;
  toolName: string;
  reason?: string;
  input: unknown;
  taskId: number;
  runId: string;
}
export interface DesktopAgentState {
  accountId: number | null;
  signedIn: boolean;
  running: boolean;
  workspace: string | null;
  taskId: number | null;
  runId: string | null;
  messages: DesktopAgentMessage[];
  status?: string;
  creditsBalance?: number;
  approvals?: DesktopAgentApproval[];
}
export interface DesktopAgentBridge {
  readonly protocol: 'admoji-harness-v2';
  getState(): Promise<DesktopAgentState>;
  chooseFolder(): Promise<DesktopAgentState>;
  beginTurn(scope: { accountId: number; taskId: number }): Promise<{ runId: string }>;
  executeTool(request: { accountId: number; taskId: number; runId: string; requestId: string; toolName: string; input: unknown }): Promise<{ success: boolean; content: string }>;
  endTurn(scope: { accountId: number; taskId: number; runId: string }): Promise<unknown>;
  stop(): Promise<unknown>;
  decide(id: string, outcome: 'allowed-once' | 'rejected'): Promise<unknown>;
  onState(callback: (state: DesktopAgentState) => void): () => void;
  onApproval(callback: (request: DesktopAgentApproval) => void): () => void;
  onApprovalClosed(callback: (id: string) => void): () => void;
}
declare global { interface Window { admojiLocal?: DesktopAgentBridge; } }

export function isDesktopAgentBridge(value: unknown): value is DesktopAgentBridge {
  if (!value || typeof value !== 'object') return false;
  const bridge = value as Partial<DesktopAgentBridge>;
  return bridge.protocol === 'admoji-harness-v2' &&
    ['getState', 'chooseFolder', 'beginTurn', 'executeTool', 'endTurn', 'stop', 'decide', 'onState', 'onApproval', 'onApprovalClosed'].every(key => typeof bridge[key as keyof DesktopAgentBridge] === 'function');
}

/** Native IDs are stable across snapshots; update their originating task only. */
export function mergeDesktopAgentMessages(current: CanvasChatMessage[], state: DesktopAgentState): CanvasChatMessage[] {
  const result = current.slice();
  for (const item of state.messages ?? []) {
    if (!item || typeof item.id !== 'string' || !item.id || typeof item.text !== 'string' || !['user', 'assistant', 'tool', 'error'].includes(item.kind)) continue;
    const index = result.findIndex(message => message.id === item.id);
    const old = index >= 0 ? result[index] : undefined;
    const localDetail = item.kind === 'tool' || item.kind === 'error';
    const message: CanvasChatMessage = {
      ...old,
      id: item.id,
      role: item.kind === 'user' ? 'user' : 'assistant',
      content: item.text,
      timestamp: item.timestamp ?? old?.timestamp,
      workspaceContext: { entryMode: 'agent', creativeActivated: false },
      isAgentMessage: item.kind !== 'user',
      isStreaming: false,
      classifierReasoning: undefined,
      desktopRun: true,
      desktopAccountId: state.accountId,
      ...(localDetail ? { desktopTool: { kind: item.kind as 'tool' | 'error', label: item.label } } : {}),
    };
    if (index >= 0) result[index] = message;
    else result.push(message);
  }
  if (!state.running) {
    for (let i = 0; i < result.length; i++) {
      const message = result[i];
      if (message.desktopRun && message.isStreaming) result[i] = { ...message, isStreaming: false, classifierReasoning: undefined, content: message.content || state.status || 'Task ended.' };
    }
  }
  return result;
}

/** Cached native UI messages are readable only by their authenticated owner. */
export function desktopMessagesForAccount(messages: CanvasChatMessage[], accountId: number | null): CanvasChatMessage[] {
  return messages.filter(message => !(message.desktopRun || message.desktopTool) ||
    (Number.isSafeInteger(accountId) && accountId! > 0 && message.desktopAccountId === accountId));
}

/** Local file contents and tool arguments/results never enter shared history. */
export function sharedDesktopConversation(history: unknown[]): unknown[] {
  return history.filter(message => !(message && typeof message === 'object' && (message as { desktopTool?: unknown }).desktopTool));
}
